import { Outlet, useLocation, useParams } from "react-router";
import { useAuth } from "./auth-provider";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function isUuidV7(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function normalizeRouteTenantToOidcTenant(routeTenantId: string): string | null {
  const trimmed = routeTenantId.trim();
  if (isUuidV7(trimmed)) {
    return trimmed.toLowerCase();
  }
  // Internal control-plane tenant ids (tenant_...) are not authority tenant ids.
  // Only pass tenant_id to OIDC when the route already contains a real authority GUID v7.
  return null;
}

export function AuthGuard() {
  const { status, isAuthenticated, login, refreshSession, error, isConfigured } = useAuth();
  const location = useLocation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  const oidcTenantId = tenantId ? normalizeRouteTenantToOidcTenant(tenantId) : null;

  if (isAuthenticated) {
    return <Outlet />;
  }

  if (status === "loading") {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: "var(--bg)" }}
      >
        <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
          Restoring session...
        </p>
      </div>
    );
  }

  if (status === "provisioning") {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ background: "var(--bg)" }}
      >
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-lg">Workspace Provisioning</CardTitle>
            <CardDescription>
              This workspace is still being prepared.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button onClick={() => void refreshSession()} className="w-full">
              Refresh
            </Button>
            <p
              className="text-[11px] text-center"
              style={{ color: "var(--muted-foreground)" }}
            >
              {error || "Try again in a moment."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{ background: "var(--bg)" }}
    >
      {tenantId ? (
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-lg">Authentication Required</CardTitle>
            <CardDescription>
              Sign in through the Auth Server Control Plane to access this tenant.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button
              onClick={() =>
                void login({
                  ...(oidcTenantId ? { tenantId: oidcTenantId } : {}),
                  returnTo,
                })}
              className="w-full"
              disabled={!isConfigured}
            >
              Sign In
            </Button>
            <p
              className="text-[11px] text-center"
              style={{ color: "var(--muted-foreground)" }}
            >
              {error || (isConfigured ? "Use your tenant-bound Auth Server account." : "OIDC is not configured for this app.")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-lg">Authentication Required</CardTitle>
            <CardDescription>
              Sign in through the Auth Server Control Plane to access your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button onClick={() => void login({ returnTo })} className="w-full" disabled={!isConfigured}>
              Sign In
            </Button>
            <p
              className="text-[11px] text-center"
              style={{ color: "var(--muted-foreground)" }}
            >
              {error || (isConfigured ? "The identity provider will choose your first eligible tenant when none is specified." : "OIDC is not configured for this app.")}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
